import { createContext, useContext, useMemo, type ReactNode } from "react";
import { MapsProviderHost } from "@genius-tracker/maps/reactHost";
import { useLocale } from "@genius-tracker/i18n/react";
import { resolveMapsSetup, type MapsSetup } from "./provider.js";

/**
 * Web-app side of the maps provider wiring.
 *
 * Owns the single `MapsSetup` for the whole app so every `MapView`
 * shares one provider (one script tag for Google, one SVG sheet for
 * the fallback). Wraps `MapsProviderHost` from `@genius-tracker/maps`
 * to keep product code away from the provider type.
 *
 * E-BUNDLE-25 — der Wirt kommt aus `@genius-tracker/maps/reactHost`,
 * nicht aus `/react`. Dieser Provider haengt um den ganzen Baum, der
 * Import landet also im Entry-Chunk; `/react` haette `<MapView>` samt
 * Mount- und Kamera-Effekten mitgebracht, obwohl die Landeroute
 * /dashboard keine Karte zeigt.
 */

const Ctx = createContext<MapsSetup | null>(null);

export function MapsSetupProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  // GT-I18N-FULL-MATRIX-CLOSEOUT-PART-2-01 (D-12) — capture the active locale
  // at FIRST mount so the initial Google map renders its controls in the
  // user's language. Google reads `language` once at script load, so this is
  // intentionally NOT reactive to later locale switches (relabelling an
  // already-loaded map needs a full reload — a documented Google constraint).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fixed at first load by design
  const setup = useMemo(() => resolveMapsSetup(import.meta.env, locale), []);
  return (
    <Ctx.Provider value={setup}>
      <MapsProviderHost provider={setup.provider}>{children}</MapsProviderHost>
    </Ctx.Provider>
  );
}

export function useMapsSetup(): MapsSetup {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useMapsSetup must be used inside <MapsSetupProvider>");
  return ctx;
}
